import type { ElementType } from "react";
import type {
  AirdropFarmingBotStatus,
  AirdropFarmingBotType,
  AirdropFarmingPerpsExchangeId,
  AirdropFarmingSpotExchangeId,
  AirdropFarmingStrategy,
} from "@cede/types";
import { AIRDROP_FARMING_BOT_TYPE, AIRDROP_FARMING_STRATEGY, SUPPORTED_EXCHANGES } from "@cede/types";
import { HIP3_BUILDER_ADDRESS, HIP3_BUILDER_FEE_RATE, HIP3_BUILDER_FEE_INT } from "@cede/utils";
import { Flame, Scale, ShieldCheck } from "lucide-react";

/** Settlement coins accepted by Hyperliquid ecosystem (including HIP-3 tokens). */
export const HYPERLIQUID_SETTLEMENT_COINS = ["USDC", "USDT", "USDH", "USDE", "USDT0"] as const;

/** Default settlement coins for CEX and non-Hyperliquid exchanges. */
export const DEFAULT_SETTLEMENT_COINS = ["USDC", "USDT"] as const;

/** Top-level exchange tabs in the Launch Bot modal */
export const AIRDROP_FARMING_TAB_IDS = [
  SUPPORTED_EXCHANGES.HYPERLIQUID,
  SUPPORTED_EXCHANGES.PARADEX,
  SUPPORTED_EXCHANGES.GRVT,
  SUPPORTED_EXCHANGES.ZEROONE,
  SUPPORTED_EXCHANGES.EXTENDED,
  SUPPORTED_EXCHANGES.HIBACHI,
  SUPPORTED_EXCHANGES.BITGET,
] as const;
export type AirdropFarmingTabId = (typeof AIRDROP_FARMING_TAB_IDS)[number];

/**
 * Maps every AirdropFarmingExchangeId to its UI tab.
 * Compile-time exhaustiveness: adding a new exchange to the type
 * forces adding it here — TypeScript will error on the missing key.
 *
 * `decibel`, `hotstuff`, and `rise` are excluded because hub-v2 does
 * not surface them (Flow does). Removing them from the key type keeps the
 * Record exhaustive for the exchanges hub-v2 actually renders.
 */
export const EXCHANGE_TO_TAB: Record<
  Exclude<AirdropFarmingPerpsExchangeId, "decibel" | "hotstuff" | "rise">,
  AirdropFarmingTabId
> = {
  tradexyz: "hyperliquid",
  hyperliquid: "hyperliquid",
  paradex: "paradex",
  grvt: "grvt",
  zeroone: "zeroone",
  extended: "extended",
  hibachi: "hibachi",
  bitget: "bitget",
};

export const RISK_PROFILE_STYLES = {
  AGGRESSIVE: { icon: Flame, color: "text-red-400", bg: "bg-red-400/10" },
  BALANCED: { icon: Scale, color: "text-cede-primary", bg: "bg-cede-primary/10" },
  CONSERVATIVE: { icon: ShieldCheck, color: "text-emerald-400", bg: "bg-emerald-400/10" },
} as const satisfies Record<string, { icon: ElementType; color: string; bg: string }>;

export const BOT_STATUS_CONFIG: Record<AirdropFarmingBotStatus, { label: string }> = {
  PENDING: { label: "Starting" },
  RUNNING: { label: "Running" },
  PAUSED: { label: "Paused" },
  STOPPING: { label: "Stopping" },
  STOPPED: { label: "Stopped" },
  COMPLETED: { label: "Completed" },
  STOPPED_MAX_LOSS: { label: "Max Loss" },
  ERROR: { label: "Error" },
  PAUSED_FOR_MAINTENANCE: { label: "Paused for exchange maintenance" },
};

export type BotMode = AirdropFarmingBotType;

/** Spot fee rates per exchange (VIP 0 / default tier) */
export const SPOT_FEE_RATES: Record<AirdropFarmingSpotExchangeId, { maker: number; taker: number }> = {
  [SUPPORTED_EXCHANGES.MEXC]: { maker: 0.001, taker: 0.001 }, // 0.10%
  [SUPPORTED_EXCHANGES.KUCOIN]: { maker: 0.001, taker: 0.001 }, // 0.10%
  [SUPPORTED_EXCHANGES.CRYPTOCOM]: { maker: 0.004, taker: 0.004 }, // 0.40%
  [SUPPORTED_EXCHANGES.BYBIT]: { maker: 0.001, taker: 0.001 }, // 0.10%
};

export const MIN_NOTIONAL_PER_LEVEL = 10; // $10 exchange minimum
/** Minimum source balance required to initiate a swap ($10 HL minimum + 3% margin) */
export const MIN_SWAP_AMOUNT = 10.3;
// TODO: Fetch grid config (numLevels, leverage, minNotional) from the API
// instead of hardcoding — the backend is the single source of truth.
export const DEFAULT_NUM_LEVELS = 2;
export const DEFAULT_LEVERAGE = 3;
export const ACTIVE_BOT_STATUSES: AirdropFarmingBotStatus[] = [
  "PENDING",
  "RUNNING",
  "PAUSED",
  "STOPPING",
  "PAUSED_FOR_MAINTENANCE",
];
export const BOT_POLLING_INTERVAL_MS = 120_000; // Fallback safety net only; SSE pushes full bot state in real-time

/**
 * Conservative cost estimate per $1,000 of volume, used for max loss suggestion.
 * Actual costs from backtesting: Paradex ~$0.36, Hyperliquid ~$0.71, GRVT ~$1.50.
 * We use $1 as a safe upper bound that covers fees + adverse price moves for most exchanges.
 */
export const SUGGESTED_COST_PER_1K_USD = 1;

export const RISK_PROFILES = [
  { value: "AGGRESSIVE", label: "Aggressive", description: "Max volume" },
  { value: "BALANCED", label: "Balanced", description: "Good balance" },
  { value: "CONSERVATIVE", label: "Conservative", description: "Safety first" },
] as const;

/**
 * Rough estimate of grid cycles per hour by risk profile.
 * Based on QA runs: tighter spreads fill faster.
 * These are conservative estimates used only for pre-launch ETA display.
 */
export const ESTIMATED_CYCLES_PER_HOUR: Record<string, number> = {
  AGGRESSIVE: 20,
  BALANCED: 12,
  CONSERVATIVE: 6,
};

/** Per-strategy display config for the launch bot form. */
export interface StrategyConfig {
  id: AirdropFarmingStrategy;
  name: string;
  tagline: string;
  badge: string;
  /** Tailwind color classes */
  colors: {
    border: string;
    bg: string;
    iconColor: string;
    hoverBorder: string;
    hoverBg: string;
    ring: string;
    accent: string;
    badgeBg: string;
    badgeText: string;
  };
}

export const DEFAULT_STRATEGY = AIRDROP_FARMING_STRATEGY.DEPTH_PULSE;

/**
 * Per-strategy display config. Partial — strategies not listed here are hidden from the UI.
 * Adaptive Shield removed after 275K-simulation backtesting showed inconsistent performance.
 */
export const STRATEGY_BY_ID: Partial<Record<AirdropFarmingStrategy, StrategyConfig>> = {
  [AIRDROP_FARMING_STRATEGY.DEPTH_PULSE]: {
    id: AIRDROP_FARMING_STRATEGY.DEPTH_PULSE,
    name: "Depth Pulse",
    tagline: "Best for quick volume on tokens with deep liquidity.",
    badge: "Volume Machine",
    colors: {
      border: "border-blue-500/60",
      bg: "bg-blue-500/10",
      iconColor: "text-blue-400",
      hoverBorder: "hover:border-blue-500/40",
      hoverBg: "hover:bg-blue-500/5",
      ring: "ring-blue-500/20",
      accent: "text-blue-400",
      badgeBg: "bg-blue-500/15",
      badgeText: "text-blue-400",
    },
  },
  [AIRDROP_FARMING_STRATEGY.VOLATILITY_SURFER]: {
    id: AIRDROP_FARMING_STRATEGY.VOLATILITY_SURFER,
    name: "Volatility Surfer",
    tagline: "Best when price is volatile.",
    badge: "Best for Metals",
    colors: {
      border: "border-cyan-500/60",
      bg: "bg-cyan-500/10",
      iconColor: "text-cyan-400",
      hoverBorder: "hover:border-cyan-500/40",
      hoverBg: "hover:bg-cyan-500/5",
      ring: "ring-cyan-500/20",
      accent: "text-cyan-400",
      badgeBg: "bg-cyan-500/15",
      badgeText: "text-cyan-400",
    },
  },
  [AIRDROP_FARMING_STRATEGY.FLOW_GUARD]: {
    id: AIRDROP_FARMING_STRATEGY.FLOW_GUARD,
    name: "Flow Guard",
    tagline: "Best for low-risk taker who prioritize safety over speed.",
    badge: "Ultra-Safe",
    colors: {
      border: "border-purple-500/60",
      bg: "bg-purple-500/10",
      iconColor: "text-purple-400",
      hoverBorder: "hover:border-purple-500/40",
      hoverBg: "hover:bg-purple-500/5",
      ring: "ring-purple-500/20",
      accent: "text-purple-400",
      badgeBg: "bg-purple-500/15",
      badgeText: "text-purple-400",
    },
  },
  [AIRDROP_FARMING_STRATEGY.BOUNCE_CATCHER]: {
    id: AIRDROP_FARMING_STRATEGY.BOUNCE_CATCHER,
    name: "Bounce Catcher",
    tagline: "Best when the token price keeps bouncing within a range.",
    badge: "Lowest Avg Loss",
    colors: {
      border: "border-emerald-500/60",
      bg: "bg-emerald-500/10",
      iconColor: "text-emerald-400",
      hoverBorder: "hover:border-emerald-500/40",
      hoverBg: "hover:bg-emerald-500/5",
      ring: "ring-emerald-500/20",
      accent: "text-emerald-400",
      badgeBg: "bg-emerald-500/15",
      badgeText: "text-emerald-400",
    },
  },
};

/** Ordered list of strategies for display in the launch bot form. */
export const STRATEGIES: StrategyConfig[] = Object.values(STRATEGY_BY_ID).filter(Boolean) as StrategyConfig[];

export const AIRDROP_FARMING_DOCS_URL = "https://docs.cedehub.io/user-guide/perps-algo-trading-bots";

export const CEDE_FLOW_URL = "https://cedeflow.io";
/**
 * Per-environment, because the arena redirect is unconditional: hardcoded to
 * production, every preview and develop deploy bounced its own visitors into
 * production arena. Read inline so `NEXT_PUBLIC_*` inlines at build time.
 */
export const CEDE_ARENA_URL = process.env.NEXT_PUBLIC_CEDE_ARENA_URL || "https://www.cedearena.io";

/** Arena persists `?utm=<value>` from the landing URL as a user source. */
export const arenaUrlWithUtm = (utm: string) => `${CEDE_ARENA_URL}/?utm=${utm}`;

/** Maintenance messages shown when bot creation or exchange access is temporarily paused. */
export const MAINTENANCE_MESSAGES = {
  BOT_CREATION_DISABLED: "Bot creation is temporarily paused while we improve stability. Please come back later.",
  EXCHANGE_MAINTENANCE: (exchangeLabel: string) =>
    `${exchangeLabel} is currently under maintenance. Please come back soon.`,
  DISCORD_STATUS_TEXT: "Check status on Discord",
} as const;

export { HIP3_BUILDER_ADDRESS, HIP3_BUILDER_FEE_RATE, HIP3_BUILDER_FEE_INT };

/** Builder fee as a decimal rate. HIP3_BUILDER_FEE_INT is in tenths of bps (5 → 0.005% → 0.00005). */
const HIP3_BUILDER_FEE_DECIMAL = HIP3_BUILDER_FEE_INT / 100_000;

/** Extended builder fee: 0.005% = 0.00005 (matches EXTENDED_CONFIG.BUILDER_FEE in apps/api) */
const EXTENDED_BUILDER_FEE_DECIMAL = 0.00005;

/**
 * All settlement/collateral coins used by supported exchanges.
 * Used to compute combined USD balance across all coins.
 */
export const SETTLEMENT_COINS = ["USDC", "USDH", "USDE", "USDT0", "USDT"] as const;
export type SettlementCoin = (typeof SETTLEMENT_COINS)[number];

/**
 * Per-exchange display and behaviour config for the airdrop farming UI.
 * Adding a new exchange? Just add an entry here and a connection hook.
 */
export interface AirdropFarmingExchangeConfig {
  name: string;
  /** Display label for the exchange selector tab (may differ from name, e.g. "TradeXYZ") */
  label: string;
  /** Optional sub-label shown in the exchange selector tab (e.g. "HIP-3") */
  sublabel?: string;
  icon: string;
  accountLabel: string;
  /** Margin mode used for positions on this exchange */
  marginMode: "cross" | "isolated";
  /** Fee rates configuration — exchanges with a growth mode can provide an alternate set */
  feeRates: {
    default: { maker: number; taker: number };
    /** Reduced fee rates for growth-mode markets (e.g. HIP-3 on TradeXYZ) */
    growth?: { maker: number; taker: number };
  };
  /** Fee disclaimer shown on the market selection step */
  feeDisclaimer: {
    text: string;
    linkText: string;
    linkUrl: string;
  };
  /** wagmi chain ID required for agent approval. If unset, no network switch needed. */
  requiredChainId?: number;
  requiredChainName?: string;
  /** URL where users manage their agent wallets on the exchange. */
  agentManagementUrl?: string;
  /** URL where users can deposit funds or manage positions on the exchange. */
  depositUrl?: string;
  /** Settlement coin for this exchange (defaults to "USDC" if not set). */
  settlementCoin?: string;
}

export type PositionManagementUrlType = "depositUrl" | "agentManagementUrl";

export function getPositionManagementLink(config?: AirdropFarmingExchangeConfig): {
  url: string;
  urlType: PositionManagementUrlType;
} | null {
  if (!config) return null;
  if (config.depositUrl) {
    return { url: config.depositUrl, urlType: "depositUrl" };
  }
  if (config.agentManagementUrl) {
    return { url: config.agentManagementUrl, urlType: "agentManagementUrl" };
  }
  return null;
}

// --- HIP-3 Sub-Platforms ---

export const DEFAULT_HIP3_FEE_RATES = {
  GROWTH: { maker: 0.00003, taker: 0.00009 },
  STANDARD: { maker: 0.0003, taker: 0.0009 },
};

export const HIP3_PLATFORMS = {
  tradexyz: {
    id: "tradexyz",
    label: "TradeXYZ",
    logo: "/dexs/tradexyz.svg",
    dex: "xyz",
    settlementCoin: "USDC" as SettlementCoin,
    feeRates: DEFAULT_HIP3_FEE_RATES,
  },
} as const;

export type Hip3PlatformId = keyof typeof HIP3_PLATFORMS;
export const HIP3_PLATFORM_IDS = Object.keys(HIP3_PLATFORMS) as Hip3PlatformId[];
export const isHip3Platform = (id: string): id is Hip3PlatformId => id in HIP3_PLATFORMS;
export const DEFAULT_HIP3_PLATFORM: Hip3PlatformId = "tradexyz";

/**
 * Resolves the applicable fee rates for a market based on exchange type, platform, and growth mode.
 *
 * - HIP-3 platforms: uses platform-specific GROWTH or STANDARD rates
 * - Other exchanges: uses exchange config growth rates (if available and growth mode) or default rates
 */
export function getMarketFeeRates(
  isHip3: boolean,
  isGrowthMode: boolean,
  exchangeConfig: AirdropFarmingExchangeConfig,
  hip3Platform?: Hip3PlatformId,
): { maker: number; taker: number } {
  if (isHip3 && hip3Platform) {
    const platformFees = HIP3_PLATFORMS[hip3Platform].feeRates;
    const base = isGrowthMode ? platformFees.GROWTH : platformFees.STANDARD;
    // HIP-3 builder fee is charged on top of exchange fees
    return {
      maker: base.maker + HIP3_BUILDER_FEE_DECIMAL,
      taker: base.taker + HIP3_BUILDER_FEE_DECIMAL,
    };
  }
  return isGrowthMode && exchangeConfig.feeRates.growth
    ? exchangeConfig.feeRates.growth
    : exchangeConfig.feeRates.default;
}

export const AIRDROP_FARMING_EXCHANGE_CONFIG: Record<AirdropFarmingTabId, AirdropFarmingExchangeConfig> = {
  [SUPPORTED_EXCHANGES.HYPERLIQUID]: {
    name: "Hyperliquid",
    label: "Hyperliquid",
    sublabel: "HIP-3",
    icon: "hyperliquid",
    accountLabel: "Hyperliquid Account",
    marginMode: "isolated",
    feeRates: {
      // Tier 0 — source: https://hyperliquid.gitbook.io/hyperliquid-docs/trading/fees
      default: { maker: 0.0002, taker: 0.00035 },
      // Growth (HIP-3): ~90% reduction from standard rates
      growth: { maker: 0.00003, taker: 0.00009 },
    },
    feeDisclaimer: {
      text: "*Estimated fees at Tier 0. Actual fees may vary based on volume tier and",
      linkText: "HIP-3 deployer fees",
      linkUrl: "https://hyperliquid.gitbook.io/hyperliquid-docs/trading/fees",
    },
    requiredChainId: 42161,
    requiredChainName: "Arbitrum",
    agentManagementUrl: "https://app.hyperliquid.xyz/API",

    depositUrl: "https://app.hyperliquid.xyz/portfolio",
  },
  [SUPPORTED_EXCHANGES.PARADEX]: {
    name: "Paradex",
    label: "Paradex",
    icon: "paradex",
    accountLabel: "Paradex Account",
    marginMode: "isolated",
    feeRates: {
      // Pro trader (API) tier — source: https://docs.paradex.trade/trading/trading-fees#spot-and-perpetual-futures
      default: { maker: 0.00003, taker: 0.0002 },
    },
    feeDisclaimer: {
      text: "*Estimated fees at Pro trader (API) tier. Actual fees may vary based on your",
      linkText: "Paradex fee tier",
      linkUrl: "https://docs.paradex.trade/trading/trading-fees#spot-and-perpetual-futures",
    },
    requiredChainId: 1,
    requiredChainName: "Ethereum",

    depositUrl: "https://app.paradex.trade/portfolio",
  },
  [SUPPORTED_EXCHANGES.GRVT]: {
    name: "GRVT",
    label: "GRVT",
    icon: "grvt",
    accountLabel: "GRVT Account",
    marginMode: "isolated",
    settlementCoin: "USDT",
    feeRates: {
      // Level 1 (default) — source: https://help.grvt.io/en/articles/9614699-how-does-grvt-s-fee-model-work
      // Includes 0.5 bps builder fee on top of exchange fees
      default: { maker: -0.000001 + HIP3_BUILDER_FEE_DECIMAL, taker: 0.00045 + HIP3_BUILDER_FEE_DECIMAL },
    },
    feeDisclaimer: {
      text: "*Fees based on your current GRVT tier. See",
      linkText: "GRVT fee tiers",
      linkUrl: "https://help.grvt.io/en/articles/9614699-how-does-grvt-s-fee-model-work",
    },
    requiredChainId: 325,
    requiredChainName: "GRVT",
    agentManagementUrl: "https://grvt.io/exchange/account/api-keys",

    depositUrl: "https://grvt.io/exchange/portfolio",
  },
  zeroone: {
    name: "zeroone",
    label: "01.xyz",
    icon: "zeroone",
    accountLabel: "01 Account",
    marginMode: "cross",
    feeRates: {
      default: { maker: 0.0001, taker: 0.00035 },
    },
    feeDisclaimer: {
      text: "*Estimated fees based on default tier. See",
      linkText: "01.xyz Fees",
      linkUrl: "https://docs.01.xyz/support/faq/general#what-are-the-fees-for-trading-on-01",
    },
    // No requiredChainId — this is Solana, not EVM
    settlementCoin: "USDC",
    depositUrl: "https://01.xyz/portfolio",
  },
  extended: {
    name: "Extended",
    label: "Extended",
    icon: "extended",
    accountLabel: "Extended Account",
    marginMode: "cross",
    feeRates: {
      // Includes 0.5 bps builder fee on top of exchange fees
      default: { maker: 0 + EXTENDED_BUILDER_FEE_DECIMAL, taker: 0.00025 + EXTENDED_BUILDER_FEE_DECIMAL },
    },
    feeDisclaimer: {
      text: "*Estimated fees include 0.5 bps builder fee. See",
      linkText: "Extended Fees",
      linkUrl: "https://docs.extended.exchange/extended-resources/trading/trading-fees-and-rebates",
    },
    requiredChainId: 1,
    requiredChainName: "Ethereum",
    settlementCoin: "USDC",
    depositUrl: "https://app.extended.exchange/portfolio/overview",
  },
  hibachi: {
    name: "Hibachi",
    label: "Hibachi",
    icon: "hibachi",
    accountLabel: "Hibachi Account",
    marginMode: "cross",
    settlementCoin: "USDT",
    feeRates: {
      default: { maker: 0, taker: 0.00045 },
    },
    feeDisclaimer: {
      text: "*Estimated fees at default tier. Actual fees may vary. See",
      linkText: "Hibachi Fees",
      linkUrl: "https://docs.hibachi.xyz/hibachi-docs/trading/fees",
    },
    // No requiredChainId — users provide API keys directly, no chain switching needed
  },
  bitget: {
    name: "Bitget",
    label: "Bitget",
    icon: "bitget",
    accountLabel: "Bitget Account",
    marginMode: "cross",
    feeRates: {
      // VIP 0 — source: https://www.bitget.com/fee
      default: { maker: 0.0002, taker: 0.0006 },
    },
    feeDisclaimer: {
      text: "*Estimated fees at VIP 0 tier. Actual fees may vary based on your",
      linkText: "Bitget fee tier",
      linkUrl: "https://www.bitget.com/fee",
    },
    depositUrl: "https://www.bitget.com/asset/overview",
    // No requiredChainId — users provide API keys directly, no chain switching needed
    // No settlementCoin since it depends on the selected pair
  },
};

/** Check if a bot mode is spot. */
export const isSpotMode = (mode: BotMode): boolean => mode === AIRDROP_FARMING_BOT_TYPE.SPOT;

// --- Bot config re-exports ---
export type {
  BotTypeConfig,
  BotType,
  AnyBot,
  StatusStyleEntry,
  SortOption,
  BotFieldAccessors,
  SortKey,
  SortDir,
} from "./bot-config-types";
export { SHARED_STATUS_STYLES } from "./bot-shared-styles";
export { GRID_BOT_CONFIG } from "./grid-config";
export { DN_BOT_CONFIG } from "./dn-config";
export { BotStrategy, SWAP_SETTLEMENT_DELAY_MS, TWO_HOP_SLIPPAGE_FACTOR } from "./bot-constants";

/** Exchange options derived from the tab config — used in DN leg selectors and elsewhere. */
export const EXCHANGE_OPTIONS = AIRDROP_FARMING_TAB_IDS.map((id: AirdropFarmingTabId) => {
  const config = AIRDROP_FARMING_EXCHANGE_CONFIG[id];
  return { id, label: config.label, icon: config.icon, sublabel: config.sublabel };
});
